// src/components/quotation/QuotationForm.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useGetAllCustomersQuery,
  useAddCustomerMutation, // (kept as-is; if unused you can remove)
} from "../../toolkitServices/customerApi";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { handleDropDowns } from "../../config/dropDownHelpers";
import {
  defaultDiamondFields,
  defaultStoneFields,
  QuotationTopFields,
} from "../../config/fieldsHelper";
import Select from "react-select";
import { motion } from "framer-motion";
import {
  convertToDateInputFormat,
  formatDate,
  getTodayDate,
  threeDecimal,
} from "../../config/helperFunctions";
import { FaPlus, FaUndo, FaList, FaCheck } from "react-icons/fa";
import { FaCartShopping } from "react-icons/fa6";

import InvoiceItemForm from "../NewInvoice/InvoiceItemForm";
import LooseStoneItemForm from "../NewInvoice/LooseStoneItemForm";
import LooseDiamondItemForm from "../NewInvoice/LooseDiamondItemForm";
import InvoiceItemTypeTabs from "../NewInvoice/InvoiceItemTypeTabs";

import { gst as ORNAMENT_GST, tds } from "../../config/taxes";
import QuickAddCustomerModal from "../other/QuickAddCustomerModal";
import CustomerAvatarHoverCard from "../other/CustomerAvatarHoverCard";
import {
  useAddQuotationMutation,
  useUpdateQuotationMutation,
  useGetLastQuotationNumberQuery,
  useDownloadQuotationPdfMutation,
} from "../../toolkitServices/quotationApi";
import toast from "react-hot-toast";

// Quotation PDF open helper
import ConfirmDialog from "../ConfirmDialog";
import InvoicePrintPreviewModal from "../InvoicePrintPreviewModal";
import {
  useGetTenantQuotationTemplateQuery,
} from "../../toolkitServices/invoiceTemplateApi";
import { useGetOtherSettingsQuery } from "../../toolkitServices/otherSettingsApi";

const CARD_HEIGHT_CLASS = "h-[62vh] sm:h-[32vh]";
const today = convertToDateInputFormat(getTodayDate());

const DEFAULTS = {
  customer: null,
  branch: null,
  quotationNo: "",
  quotationDate: convertToDateInputFormat(getTodayDate()),
  validTill: convertToDateInputFormat(getTodayDate()),
  stockEntries: [],
  looseStones: [],
  looseDiamonds: [],
  totalDiscount: "",
  courierCharge: "",
  fineGold: "",
  fineSilver: "",
  advanceAmount: "",
  balanceAmount: "",
};

const baseSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "30px",
    height: "30px",
    borderColor: state.isFocused ? "#9481ce" : "#D1D5DB",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(148,129,206,.25)" : "none",
  }),
  menu: (b) => ({ ...b, zIndex: 40, fontSize: "12px" }),
  menuList: (base) => ({ ...base, maxHeight: "120px", overflowY: "auto" }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 6px",
    height: "30px",
    overflow: "hidden",
  }),
  indicatorsContainer: (base) => ({ ...base, height: "30px" }),
  dropdownIndicator: (base) => ({ ...base, padding: "4px" }),
  clearIndicator: (base) => ({ ...base, padding: "4px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
};

const customerSelectStyles = {
  ...baseSelectStyles,
  container: (base) => ({ ...base, width: "100%" }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "calc(100% - 28px)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
  menuList: (base) => ({ ...base, maxHeight: "180px", overflowY: "auto" }),
};

const withErrorBorder = (stylesObj, hasError) => {
  if (!hasError) return stylesObj;
  return {
    ...stylesObj,
    control: (base, state) => {
      const baseCtrl = stylesObj.control
        ? stylesObj.control(base, state)
        : base;
      return {
        ...baseCtrl,
        borderColor: "#ef4444",
        boxShadow: state.isFocused ? "0 0 0 2px rgba(239,68,68,0.20)" : "none",
        "&:hover": { borderColor: "#ef4444" },
      };
    },
  };
};

const inputCls = (hasError, extra = "", type = "") =>
  `w-full px-3 ${
    type === "date" ? "py-[3px]" : "py-1"
  } border rounded-md text-sm focus:outline-none focus:ring-2 ${
    hasError
      ? "border-red-400 focus:ring-red-300"
      : "border-gray-300 focus:ring-violet-400"
  } ${extra}`;

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const QuotationForm = ({
  existingData = null,
  mode = "create",
  itemId = null,
  allowMultipleRows = true,
}) => {
  const navigate = useNavigate();

  const [addQuotation, addState] = useAddQuotationMutation();
  const [updateQuotation, updateState] = useUpdateQuotationMutation();
  const [downloadQuotationPdf] = useDownloadQuotationPdfMutation();

const [quotationPreview, setQuotationPreview] = useState({
  open: false,
  html: "",
  loading: false,
});

const [printPrompt, setPrintPrompt] = useState({
  open: false,
  quotationId: null,
});
  const { data: settingResp } = useGetOtherSettingsQuery();
const { data: selRes } = useGetTenantQuotationTemplateQuery(undefined, {
  refetchOnMountOrArgChange: false,
  refetchOnFocus: false,
  refetchOnReconnect: false,
});
  const ornamentsListRef = useRef(null);
  const looseStoneListRef = useRef(null);
  const looseDiamondListRef = useRef(null);
  const rowFirstRefs = useRef({});
    const limit = 2;

  const [customerSearch, setCustomerSearch] = useState("");
  const prevBranchIdRef = useRef(null);

  const [quickOpen, setQuickOpen] = useState(false);
  const [tinyArgs, setTinyArgs] = useState(null); // ✅ for tiny fetch like Invoice
  const lastCreatedRef = useRef(null);

  const customerSelectRef = useRef(null);
  const branchSelectRef = useRef(null);

  const [bulkRate, setBulkRate] = useState("");
  const [bulkHallmark, setBulkHallmark] = useState("");
  const [sumOpen, setSumOpen] = useState(false);
  const summaryRef = useRef(null);

  const [itemType, setItemType] = useState("ornaments"); // ornaments | loose | looseDiamond
  const authUser = useSelector((s) => s.auth?.user);

   

  const categoriesFromState = useSelector((s) => s.categories?.categories);
  const productsFromState = useSelector((s) => s.products?.products);
  const designFromState = useSelector((s) => s.designs?.designs);
  const purityFromState = useSelector((s) => s.purities?.purities);
  const branchesFromState = useSelector((s) => s.branches?.branches);
  const lastestRates = useSelector((s) => s.lastestRates);

  const {
    control,
    handleSubmit,
    register,
    setValue,
    reset,
    formState: { errors },
    getValues,
    watch,
    clearErrors,
    unregister,
    trigger,
  } = useForm({
    shouldUnregister: false,
    defaultValues: DEFAULTS,
  });

  // ✅ Branch & dates (needed before customer query)
  const branchSel = watch("branch");
  const quotationDate = watch("quotationDate");

  // ✅ Branch ko sabse pehle lane ke liye sort (Invoice jaisa)
  const topFields = useMemo(() => {
    if (!QuotationTopFields) return [];
    const arr = [...QuotationTopFields];
    arr.sort((a, b) => {
      if (a.name === "branch" && b.name !== "branch") return -1;
      if (b.name === "branch" && a.name !== "branch") return 1;
      return 0;
    });
    return arr;
  }, []);

  // ✅ Customers: branch-wise filter + branch select hone ke baad hi API hit
  const {
    data: customerResp,
    isFetching: loadingCustomers,
    refetch: refetchCustomers,
  } = useGetAllCustomersQuery(
    {
      page: 1,
      limit: limit,
      sortOrder: "desc",
      sortBy: "createdAt",
      search: customerSearch,
      branchId: branchSel?.value || undefined, // ✅ filter by branch
    },
    {
      skip: !branchSel?.value, // ✅ branch select hone ke baad hi hit kare
    },
  );

  const customers = customerResp?.data || [];

  // ✅ Tiny fetch (Invoice jaisa) - freshly created customer ko server se re-pull karke select
  const { data: tinyResp } = useGetAllCustomersQuery(tinyArgs, {
    skip: !tinyArgs,
  });

  useEffect(() => {
    let bid = authUser?.employeeDetails?.branch?._id;
    let cid = authUser?.employeeDetails?.counter?._id;
    let bname = authUser?.employeeDetails?.branch?.branchName;
    let cname = authUser?.employeeDetails?.counter?.counterName;
    if (mode != "edit" && authUser?.role == "staff" && bid) {
      setValue("branch", { value: bid, label: bname });
    }
  }, [authUser, mode]);

  useEffect(() => {
    if (!tinyArgs || !tinyResp?.data) return;

    const created = lastCreatedRef.current;
    const newest =
      tinyResp.data.find((c) => String(c?._id) === String(created?._id)) ||
      tinyResp.data[0];

    if (newest) {
      const opt =
        (handleDropDowns({ key: "customer", customers: [newest] }) || [])[0] ||
        null;
      if (opt) onCustomerPicked(opt); // ✅ now dropdown me selected dikhega
    }

    setTinyArgs(null);
  }, [tinyResp, tinyArgs]);

  // EDIT MODE HYDRATION: sirf ek baar per quotation _id
  const hydratedIdRef = useRef(null);

  useEffect(() => {
    if (mode !== "edit") return;
    if (!existingData || !existingData._id) return;

    if (hydratedIdRef.current !== existingData._id) {
      const patch = {
        ...DEFAULTS,
        ...existingData,
      };

      if (existingData.quotationDate)
        patch.quotationDate = existingData.quotationDate;
      if (existingData.validTill) patch.validTill = existingData.validTill;
      if (!patch.quotationNo && existingData.quotationNo)
        patch.quotationNo = existingData.quotationNo;

      reset(patch);

      if (patch.quotationNo) setValue("quotationNo", patch.quotationNo);
      if (patch.quotationDate) setValue("quotationDate", patch.quotationDate);
      if (patch.validTill) setValue("validTill", patch.validTill);

      hydratedIdRef.current = existingData._id;
    }
  }, [mode, existingData, reset, setValue]);

  const {
    fields: ornamentFields,
    append: ornamentAppend,
    remove: ornamentRemove,
  } = useFieldArray({ control, name: "stockEntries" });

  const {
    fields: looseFields,
    append: looseAppend,
    remove: looseRemove,
  } = useFieldArray({ control, name: "looseStones" });

  const {
    fields: looseDiaFields,
    append: looseDiaAppend,
    remove: looseDiaRemove,
  } = useFieldArray({ control, name: "looseDiamonds" });

  const customer = useWatch({ control, name: "customer" });
  const entries = useWatch({ control, name: "stockEntries" }) || [];
  const looseArr = useWatch({ control, name: "looseStones" }) || [];
  const looseDiaArr = useWatch({ control, name: "looseDiamonds" }) || [];
  const payments = useWatch({ control, name: "payments" }) || [];

  // ✅ Branch change -> clear rows + customer (Invoice jaisa)
  useEffect(() => {
    const currentBranchId = branchSel?.value || null;
    if (prevBranchIdRef.current === null) {
      prevBranchIdRef.current = currentBranchId;
      return;
    }
    if (prevBranchIdRef.current !== currentBranchId) {
      setValue("stockEntries", []);
      setValue("looseStones", []);
      setValue("looseDiamonds", []);
      setValue("customer", null); // ✅ branch change -> customer reset
      setCustomerSearch("");
      setTinyArgs(null); // ✅ safety
      lastCreatedRef.current = null;
    }
    prevBranchIdRef.current = currentBranchId;
  }, [branchSel?.value, setValue]);

  // Get next quotation number (skip when edit)
  const { data: lastNoResp } = useGetLastQuotationNumberQuery(
    branchSel?.value && quotationDate
      ? { branchId: branchSel.value, date: quotationDate }
      : undefined,
    { skip: mode === "edit" || !branchSel?.value || !quotationDate },
  );
  const nextNumber = lastNoResp?.nextNumber;

  // ============================== AGGREGATE ==============================
  const agg = useMemo(() => {
    const out = {
      ornPretaxNonMrp: 0,
      ornMrpFinal: 0,
      stonePretaxNonMrp: 0,
      stoneMrpFinal: 0,
      stoneGstWeightedNumerator: 0,
      diaPretaxNonMrp: 0,
      diaMrpFinal: 0,
      diaGstWeightedNumerator: 0,
      totalFineWt: 0,
      fineOnlyWt: 0,
    };

    for (const it of entries) {
      const mrpVal = num(it?.mrp);
      const itemAmount = num(it?.itemAmount);
      const fineOnly = num(it?.fineWt);
      const finePlusW = num(it?.fineWastageWt);

      if (mrpVal > 0) out.ornMrpFinal += mrpVal;
      else out.ornPretaxNonMrp += itemAmount;

      if (fineOnly || finePlusW) {
        out.fineOnlyWt += fineOnly;
        out.totalFineWt += finePlusW || fineOnly;
      }
    }

    for (const ls of looseArr) {
      const mrpVal = num(ls?.mrp);
      const itemAmount = num(ls?.itemAmount);
      const gstRate = Math.max(0, num(ls?.gstRate));

      if (mrpVal > 0) out.stoneMrpFinal += mrpVal;
      else {
        out.stonePretaxNonMrp += itemAmount;
        out.stoneGstWeightedNumerator += itemAmount * gstRate;
      }
    }

    for (const d of looseDiaArr) {
      const mrpVal = num(d?.mrp);
      const itemAmount = num(d?.itemAmount);
      const gstRate = Math.max(0, num(d?.gstRate));

      if (mrpVal > 0) out.diaMrpFinal += mrpVal;
      else {
        out.diaPretaxNonMrp += itemAmount;
        out.diaGstWeightedNumerator += itemAmount * gstRate;
      }
    }

    out.ornPretaxNonMrp = +out.ornPretaxNonMrp.toFixed(2);
    out.ornMrpFinal = +out.ornMrpFinal.toFixed(2);

    out.stonePretaxNonMrp = +out.stonePretaxNonMrp.toFixed(2);
    out.stoneMrpFinal = +out.stoneMrpFinal.toFixed(2);
    out.stoneGstWeightedNumerator = +out.stoneGstWeightedNumerator.toFixed(4);

    out.diaPretaxNonMrp = +out.diaPretaxNonMrp.toFixed(2);
    out.diaMrpFinal = +out.diaMrpFinal.toFixed(2);
    out.diaGstWeightedNumerator = +out.diaGstWeightedNumerator.toFixed(4);

    out.totalFineWt = +out.totalFineWt.toFixed(3);
    out.fineOnlyWt = +out.fineOnlyWt.toFixed(3);

    return out;
  }, [entries, looseArr, looseDiaArr]);

  // Ornaments-only summary
  const ornSummary = useMemo(() => {
    let rows = entries.length;
    let qty = 0,
      pieces = 0,
      stonePcs = 0,
      diamondPcs = 0,
      totalWt = 0,
      packing = 0,
      gross = 0,
      stoneWt = 0,
      diamondGram = 0,
      netWt = 0,
      stoneAmt = 0,
      diamondAmt = 0,
      hallmarkAmt = 0,
      itemAmt = 0,
      itemGstAmt = 0,
      finalAmt = 0,
      mrp = 0;

    for (const it of entries) {
      qty += num(it?.qty);
      pieces += num(it?.pieces);
      stonePcs += num(it?.stonePcs);
      diamondPcs += num(it?.diamondPcs);

      const tw = num(it?.totalWt);
      const pw = num(it?.packingWt);
      const stw = num(it?.stoneWt);
      const dCt = num(it?.diamondWt);
      const dGm = +(dCt * 0.2);

      totalWt += tw;
      packing += pw;
      gross += +(tw - pw);
      stoneWt += stw;
      diamondGram += dGm;

      const nwt = +(tw - pw - stw - dGm);
      netWt += nwt;

      stoneAmt += num(it?.stoneAmount);
      diamondAmt += num(it?.diamondAmount);
      hallmarkAmt += num(it?.hallmarkAmount);
      itemAmt += num(it?.itemAmount);
      itemGstAmt += num(it?.itemGstAmount);
      finalAmt += num(it?.totalItemAmount);
      mrp += num(it?.mrp);
    }

    return {
      rows,
      qty: Math.round(qty),
      pieces: Math.round(pieces),
      stonePcs: Math.round(stonePcs),
      diamondPcs: Math.round(diamondPcs),
      totalWt: +totalWt.toFixed(3),
      packingWt: +packing.toFixed(3),
      grossWt: +gross.toFixed(3),
      stoneWt: +stoneWt.toFixed(3),
      diamondGram: +diamondGram.toFixed(3),
      netWt: +netWt.toFixed(3),
      stoneAmt: +stoneAmt.toFixed(2),
      diamondAmt: +diamondAmt.toFixed(2),
      hallmarkAmt: +hallmarkAmt.toFixed(2),
      itemAmt: +itemAmt.toFixed(2),
      itemGstAmt: +itemGstAmt.toFixed(2),
      finalAmt: +finalAmt.toFixed(2),
      mrp: +mrp.toFixed(2),
    };
  }, [entries]);

  useEffect(() => {
    const onDoc = (e) => {
      if (summaryRef.current && !summaryRef.current.contains(e.target)) {
        setSumOpen(false);
      }
    };
    const onEsc = (e) => e.key === "Escape" && setSumOpen(false);
    if (sumOpen) {
      document.addEventListener("mousedown", onDoc);
      document.addEventListener("keydown", onEsc);
    }
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [sumOpen]);
   const applyInvoiceDiscountItemWise = () => {
      const items = getValues("stockEntries") || [];
      if (!items.length) return;
  
      const totalInvDiscount = num(getValues("totalDiscount"));
  
      const nonMrp = items
        .map((it, idx) => ({ it, idx }))
        // .filter((x) => num(x.it?.mrp) <= 0);
  
      const totalPreTax = nonMrp.reduce((s, x) => s + num(x.it?.itemAmount), 0);
  
      // 🔹 NO DISCOUNT CASE
      if (totalInvDiscount === 0 ) {
        items.forEach((it, i) => {
          // if (num(it?.mrp) > 0) return;
          const mrpVal = num(it?.mrp);
   if (mrpVal > 0) {
        const taxable = +(mrpVal / (1 + ORNAMENT_GST / 100)).toFixed(2);
        const gstAmt = +((taxable * ORNAMENT_GST) / 100).toFixed(2);
  
        setValue(`stockEntries.${i}.itemDiscountAmount`, 0);
        setValue(`stockEntries.${i}.itemAmountAfterDiscount`, taxable);
        setValue(`stockEntries.${i}.itemGstAmount`, gstAmt);
        setValue(`stockEntries.${i}.totalItemAmount`, mrpVal);
  
        return;
      }
  
          const base = num(it?.itemAmount);
          const gstAmt = +((base * ORNAMENT_GST) / 100).toFixed(2);
  
          setValue(`stockEntries.${i}.itemDiscountAmount`, 0);
          setValue(`stockEntries.${i}.itemAmountAfterDiscount`, base);
          setValue(`stockEntries.${i}.itemGstAmount`, gstAmt);
          setValue(
            `stockEntries.${i}.totalItemAmount`,
            +(base + gstAmt).toFixed(2),
          );
        });
        return;
      }
  
      // 🔹 DISCOUNT CASE
      nonMrp.forEach(({ it, idx }) => {
        const preTax = num(it.itemAmount);
        const share = preTax / totalPreTax;
  
        const disc = +(totalInvDiscount * share).toFixed(2);
        const after = +(preTax - disc).toFixed(2);
  
        const gstAmt = +((after * ORNAMENT_GST) / 100).toFixed(2);
        const finalAmt = +(after + gstAmt).toFixed(2);
  
        setValue(`stockEntries.${idx}.itemDiscountAmount`, disc);
        setValue(`stockEntries.${idx}.itemAmountAfterDiscount`, after);
        setValue(`stockEntries.${idx}.itemGstAmount`, gstAmt);
        setValue(`stockEntries.${idx}.totalItemAmount`, finalAmt);
      });
    };

  // ================= DISCOUNT & GST & TOTAL ===================
  const discount = num(watch("totalDiscount"));
  const courierCharge = num(watch("courierCharge"));
  const selectedBranch = branchSel; // ✅ same watch reuse

 const preTaxTotalNonMrp  = +(
  entries.reduce((s, it) => s + Number(it?.itemAmountAfterDiscount || 0), 0) +
  looseArr.reduce((s, it) => s + Number(it?.itemAmountAfterDiscount || 0), 0) +
  looseDiaArr.reduce((s, it) => s + Number(it?.itemAmountAfterDiscount || 0), 0)
).toFixed(2);

const taxableAmount = +(
   entries.reduce((s, it) => s + Number(it?.itemAmount || 0), 0) +
  looseArr.reduce((s, it) => s + Number(it?.itemAmount || 0), 0) +
  looseDiaArr.reduce((s, it) => s + Number(it?.itemAmount || 0), 0)
).toFixed(2);
  // const effectiveDiscount = Math.min(discount, preTaxTotalNonMrp);

  // const share = (bucket) =>
    // preTaxTotalNonMrp > 0 ? bucket / preTaxTotalNonMrp : 0;

  // const discountOrn = +(effectiveDiscount * share(agg.ornPretaxNonMrp)).toFixed(
    // 2,
  // );
  // const discountStone = +(
  //   effectiveDiscount * share(agg.stonePretaxNonMrp)
  // ).toFixed(2);
  // const discountDia = +(
  //   effectiveDiscount -
  //   discountOrn -
  //   discountStone
  // ).toFixed(2);

  // const ornAfterDiscount = +(agg.ornPretaxNonMrp - discountOrn).toFixed(2);
  // const stoneAfterDiscount = +(agg.stonePretaxNonMrp - discountStone).toFixed(
  //   2,
  // );
  // const diaAfterDiscount = +(agg.diaPretaxNonMrp - discountDia).toFixed(2);

  const salePreTax = preTaxTotalNonMrp;
    const gstOrnRate = ORNAMENT_GST;
    const gstLooseWeightedRate =
      agg.stonePretaxNonMrp > 0
        ? +(agg.stoneGstWeightedNumerator / agg.stonePretaxNonMrp).toFixed(4)
        : 0;
  


  const gstDiaWeightedRate =
    agg.diaPretaxNonMrp > 0
      ? +(agg.diaGstWeightedNumerator / agg.diaPretaxNonMrp).toFixed(4)
      : 0;

      const gstOrn = +entries.reduce(
  (sum, it) => sum + Number(it?.itemGstAmount || 0),
  0
).toFixed(2);
  // const gstOrn = +((ornAfterDiscount * ORNAMENT_GST) / 100).toFixed(2);
 const gstLoose = +looseArr.reduce(
  (sum, it) => sum + Number(it?.itemGstAmount || 0),
  0
).toFixed(2);

const gstDia = +looseDiaArr.reduce(
  (sum, it) => sum + Number(it?.itemGstAmount || 0),
  0
).toFixed(2);

 const subTotalNoCourier = +(
  ornSummary.finalAmt +
  agg.stoneMrpFinal +
  agg.diaMrpFinal
).toFixed(2);

  // const subTotalNoCourier = +(
  //   agg.ornMrpFinal +
  //   agg.stoneMrpFinal +
  //   agg.diaMrpFinal +
  //   ornAfterDiscount +
  //   stoneAfterDiscount +
  //   diaAfterDiscount +
  //   gstOrn +
  //   gstLoose +
  //   gstDia
  // ).toFixed(2);

  const computedGrandTotal = +(subTotalNoCourier + courierCharge).toFixed(2);

   const tdsApply = !!watch("tdsApply");
  const tdsBase = salePreTax;
    const tdsAmount = tdsApply ? +((tdsBase * tds) / 100).toFixed(2) : 0;
  
    const totalPayable = +(computedGrandTotal + tdsAmount).toFixed(2);
  
    const paid = +payments.reduce((s, p) => s + num(p?.amount), 0).toFixed(2);
  
    const paidFineMetal = +(
      payments?.reduce((s, p) => s + num(p?.fineMetalWt), 0) || 0
    ).toFixed(3);
    const balanceFineMetal = +(agg.totalFineWt - paidFineMetal).toFixed(3);
    const balanceNet = +(totalPayable - paid).toFixed(2);
  
    const grandTotalManual = watch("grandTotalManual");
    const manualAdjustmentMode =
  settingResp?.data?.manualAdjustmentMode || "DISCOUNT";

  // ======================================================================
 const totalDiscount = watch("totalDiscount");

  useEffect(() => {
    applyInvoiceDiscountItemWise();
  }, [
    totalDiscount,
    entries.length,
    JSON.stringify(entries.map(e => ({
  itemAmount: e.itemAmount,
  mrp: e.mrp
})))// 👈 VERY IMPORTANT
  ]);
  const registerFirstField = (id, el) => {
    if (el) rowFirstRefs.current[id] = el;
  };

  const focusRowAt = (listRef, idx) => {
    const cards = listRef.current?.querySelectorAll(".invoice-card");
    if (!cards || !cards.length) return;
    const i = Math.max(0, Math.min(idx, cards.length - 1));
    const card = cards[i];
    card?.scrollIntoView({ behavior: "smooth", block: "start" });
    const id = card?.dataset?.fieldId;
    const el = rowFirstRefs.current[id];
    if (el?.focus) el.focus();
  };

  const getFirstErrorRowIndex = (arrayName) => {
    const se = errors?.[arrayName];
    if (!se) return -1;
    const rows = Object.keys(se)
      .map((k) => Number(k))
      .sort((a, b) => a - b);
    return rows.length ? rows[0] : -1;
  };

  const getValidationPathsForActiveTab = () => {
    if (itemType === "loose") {
      const i = Math.max(0, (looseFields?.length || 1) - 1);
      if (!looseFields?.length) return [];
      return [`looseStones.${i}.counterRow`, `looseStones.${i}.stnPopupName`];
    } else if (itemType === "looseDiamond") {
      const i = Math.max(0, (looseDiaFields?.length || 1) - 1);
      if (!looseDiaFields?.length) return [];
      return [`looseDiamonds.${i}.counterRow`, `looseDiamonds.${i}.diaName`];
    }
    const i = Math.max(0, (ornamentFields?.length || 1) - 1);
    if (!ornamentFields?.length) return [];
    return [
      `stockEntries.${i}.counterRow`,
      `stockEntries.${i}.category`,
      `stockEntries.${i}.product`,
      `stockEntries.${i}.design`,
      `stockEntries.${i}.purity`,
      `stockEntries.${i}.totalWt`,
      `stockEntries.${i}.grossWt`,
      `stockEntries.${i}.netWt`,
      `stockEntries.${i}.qty`,
    ];
  };

  // Keyboard shortcuts: Ctrl+S (save) & Ctrl++ (add row)
  useEffect(() => {
    const onKeys = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        if (!addState.isLoading && !updateState.isLoading) {
          handleSubmit(onSubmit)();
        }
        return;
      }

      if (mode === "edit" || !allowMultipleRows) return;

      if (
        e.ctrlKey &&
        (e.key === "+" || e.key === "=" || e.code === "NumpadAdd")
      ) {
        e.preventDefault();

        const arrayName =
          itemType === "loose"
            ? "looseStones"
            : itemType === "looseDiamond"
              ? "looseDiamonds"
              : "stockEntries";

        const currentPaths = getValidationPathsForActiveTab();

        Promise.resolve(trigger(currentPaths)).then((valid) => {
          if (!valid) {
            setTimeout(() => {
              const badIdx = getFirstErrorRowIndex(arrayName);
              if (badIdx >= 0) {
                const ref =
                  itemType === "loose"
                    ? looseStoneListRef
                    : itemType === "looseDiamond"
                      ? looseDiamondListRef
                      : ornamentsListRef;
                focusRowAt(ref, badIdx);
              }
            }, 0);
            return;
          }

          if (itemType === "loose") {
            const nextIdx = looseFields.length;
            looseAppend({}, { shouldFocus: false });
            requestAnimationFrame(() =>
              setTimeout(() => focusRowAt(looseStoneListRef, nextIdx), 0),
            );
          } else if (itemType === "looseDiamond") {
            const nextIdx = looseDiaFields.length;
            looseDiaAppend({}, { shouldFocus: false });
            requestAnimationFrame(() =>
              setTimeout(() => focusRowAt(looseDiamondListRef, nextIdx), 0),
            );
          } else {
            const nextIdx = ornamentFields.length;
            ornamentAppend(
              {
                totalWt: "",
                stones: [defaultStoneFields],
                diamonds: [defaultDiamondFields],
              },
              { shouldFocus: false },
            );
            requestAnimationFrame(() =>
              setTimeout(() => focusRowAt(ornamentsListRef, nextIdx), 0),
            );
          }
        });
      }
    };

    window.addEventListener("keydown", onKeys);
    return () => window.removeEventListener("keydown", onKeys);
  }, [
    ornamentAppend,
    looseAppend,
    looseDiaAppend,
    ornamentFields.length,
    looseFields.length,
    looseDiaFields.length,
    mode,
    allowMultipleRows,
    handleSubmit,
    addState.isLoading,
    updateState.isLoading,
    trigger,
    itemType,
  ]);

  const focusCustomerSelect = () => {
    if (customerSelectRef.current && customerSelectRef.current.focus) {
      customerSelectRef.current.focus();
    }
  };

  const focusBranchSelect = () => {
    if (branchSelectRef.current && branchSelectRef.current.focus) {
      branchSelectRef.current.focus();
    }
  };

  const handleAddRow = async () => {
    if (mode === "edit" || !allowMultipleRows) return;

    const arrayName =
      itemType === "loose"
        ? "looseStones"
        : itemType === "looseDiamond"
          ? "looseDiamonds"
          : "stockEntries";

    const currentPaths = getValidationPathsForActiveTab();
    const valid = await trigger(currentPaths);

    if (!valid) {
      const badIdx = getFirstErrorRowIndex(arrayName);
      if (badIdx >= 0) {
        const ref =
          itemType === "loose"
            ? looseStoneListRef
            : itemType === "looseDiamond"
              ? looseDiamondListRef
              : ornamentsListRef;
        focusRowAt(ref, badIdx);
      }
      return;
    }

    if (itemType === "loose") {
      const nextIdx = looseFields.length;
      looseAppend({}, { shouldFocus: false });
      requestAnimationFrame(() =>
        setTimeout(() => focusRowAt(looseStoneListRef, nextIdx), 0),
      );
    } else if (itemType === "looseDiamond") {
      const nextIdx = looseDiaFields.length;
      looseDiaAppend({}, { shouldFocus: false });
      requestAnimationFrame(() =>
        setTimeout(() => focusRowAt(looseDiamondListRef, nextIdx), 0),
      );
    } else {
      const nextIdx = ornamentFields.length;
      ornamentAppend(
        {
          totalWt: "",
          stones: [defaultStoneFields],
          diamonds: [defaultDiamondFields],
        },
        { shouldFocus: false },
      );
      requestAnimationFrame(() =>
        setTimeout(() => focusRowAt(ornamentsListRef, nextIdx), 0),
      );
    }
  };

  // customer select logic
  const onCustomerPicked = (picked) => {
    setValue("customer", picked ?? null);
    const info = picked?.info;
    if (info) {
      setValue("fineGold", threeDecimal(info.fineGold));
      setValue("fineSilver", threeDecimal(info.fineSilver));
      setValue("advanceAmount", threeDecimal(info.advanceAmount));
      setValue("balanceAmount", threeDecimal(info.balanceAmount));
    }
  };

  const applyRateForAll = () => {
    const v = parseFloat(bulkRate);
    if (!Number.isFinite(v)) return;
    entries.forEach((_, i) => {
      setValue(`stockEntries.${i}.ratePerGram`, v);
      recalcItemRow(i);
    });
  };

  const applyHallmarkForAll = () => {
    const v = parseFloat(bulkHallmark);
    if (!Number.isFinite(v)) return;
    entries.forEach((_, i) => {
      setValue(`stockEntries.${i}.hallmarkAmount`, v);
      recalcItemRow(i);
    });
  };

  const recalcItemRow = (i) => {
    const e = (f) => `stockEntries.${i}.${f}`;
    const get = (k) => Number(getValues(e(k)) || 0);

    const totalWt = get("totalWt");
    const packingWt = get("packingWt");
    const stoneWt = get("stoneWt");
    const diamondCt = get("diamondWt");
    const ratePerGram = get("ratePerGram");
    const finePercent = get("finePercentage");
    const makingPct = get("makingPercentage");
    const makingPerGram = get("makingPerGram");
    const fixedMaking = get("fixedWastage");
    const fixedAmount = get("fixedAmount");
    const stoneAmount = get("stoneAmount");
    const diamondAmount = get("diamondAmount");
    const hallmarkAmount = get("hallmarkAmount");
    const itemDisc = get("itemDiscountAmount");

    const grossWt = +(totalWt - packingWt).toFixed(3);
    const diamondGram = +(diamondCt * 0.2).toFixed(3);
    const netWt = +(grossWt - stoneWt - diamondGram).toFixed(3);
    const fineWt = +((finePercent / 100) * netWt).toFixed(3);
    const makingBaseWt = netWt;
    const wastageWt = +((makingPct / 100) * makingBaseWt + fixedMaking).toFixed(
      3,
    );
    const fineWastageWt = +(fineWt + wastageWt).toFixed(3);

    setValue(e("grossWt"), grossWt);
    setValue(e("netWt"), netWt);
    setValue(e("fineWt"), fineWt);
    setValue(e("wastageWt"), wastageWt);
    setValue(e("fineWastageWt"), fineWastageWt);

    const metalAmount = +(ratePerGram * netWt).toFixed(2);
    const amtByFixedMaking = +(fixedMaking * ratePerGram).toFixed(2);
    const amtByMakingPerGram = +(makingPerGram * netWt).toFixed(2);
    const amtByPercentage = +(
      (makingBaseWt * ratePerGram * makingPct) /
      100
    ).toFixed(2);

    const preTaxMaking = +(
      amtByMakingPerGram +
      amtByPercentage +
      amtByFixedMaking +
      fixedAmount
    ).toFixed(2);
    const itemAmount = +(
      metalAmount +
      preTaxMaking +
      stoneAmount +
      diamondAmount +
      hallmarkAmount
    ).toFixed(2);

    const mrpVal = Number(getValues(e("mrp")) || 0);
    if (mrpVal > 0) {
      setValue(e("itemAmount"), itemAmount);
      setValue(e("itemGstAmount"), 0);
      setValue(e("totalItemAmount"), +Number(mrpVal).toFixed(2));
      return;
    }

    const itemGstAmount = +((itemAmount * ORNAMENT_GST) / 100).toFixed(2);
    const totalItemAmount = +(itemAmount + itemGstAmount - itemDisc).toFixed(2);

    setValue(e("itemAmount"), itemAmount);
    setValue(e("itemGstAmount"), itemGstAmount);
    setValue(e("totalItemAmount"), totalItemAmount);
  };

  const idOrValue = (v) =>
    v && typeof v === "object"
      ? (v.value ?? v._id ?? v.id ?? v.label ?? "")
      : (v ?? "");

  const normalizeStones = (list = []) =>
    (list || []).map((s) => ({
      ...s,
      shape: idOrValue(s.shape),
      size: idOrValue(s.size),
      color: idOrValue(s.color),
      settingType: idOrValue(s.settingType),
    }));

  const normalizeDiamonds = (list = []) =>
    (list || []).map((d) => ({
      ...d,
      shape: idOrValue(d.shape ?? d.diaPopupShape),
      size: idOrValue(d.size ?? d.diaPopupSize ?? d.diaPopupSieve),
      color: idOrValue(d.color ?? d.diaPopupColor),
      clarity: idOrValue(d.clarity ?? d.diaPopupClarity),
      pcs: Number(d.pcs ?? d.diaPopupPcs ?? 0),
      wt: Number(d.wt ?? d.diaPopupWt ?? 0),
      rateByWt: Number(d.rateByWt ?? d.diaPopupRateByWt ?? 0),
      rateByPcs: Number(d.rateByPcs ?? d.diaPopupRateByPcs ?? 0),
      amount: Number(d.amount ?? d.diaPopupAmt ?? 0),
      deduct: d.deduct ?? d.diaPopupDeduct ?? "",
      description: d.description ?? d.diaPopupDescription ?? "",
    }));

  const buildPayload = (form) => {
    const payload = { ...form };

    payload.quotationDate =
      form?.quotationDate || convertToDateInputFormat(getTodayDate());

    payload.stockEntries = (form?.stockEntries || [])
      .filter((item) => item?.category?.value)
      .map((it) => ({
        ...it,
        stones: normalizeStones(it?.stones),
        diamonds: normalizeDiamonds(it?.diamonds),
        itemCode: it?.itemCodeDetail?.value
          ? {
              value: it?.itemCodeDetail?.value,
              label: it?.itemCodeDetail?.label,
            }
          : "",
      }));

    payload.looseStones = (form?.looseStones || []).filter(
      (item) => item?.stnPopupName,
    );
    payload.looseDiamonds = (form?.looseDiamonds || []).filter(
      (item) => item?.diaName,
    );

    return payload;
  };

  const onSubmit = async (data) => {
    if (!data?.branch?.value) return toast.error("Select branch first.");
    if (!data?.customer?.value) return toast.error("Pick a customer.");
    if (!data?.quotationDate) return toast.error("Pick quotation date.");

    const payload = buildPayload(data);
payload.quotationTemplate = selRes?.quotationTemplateKey;
    try {
      if (mode === "edit" && itemId) {
        const res = await updateQuotation({ id: itemId, ...payload }).unwrap();
        const updated = res?.data ?? res;
        const qId = updated?._id || itemId;

         if (qId) {
  setPrintPrompt({ open: true, quotationId: qId });
}
        toast.success("Quotation updated.");
        setTimeout(() => navigate("/quotations/quotation-list"), 500);
      } else {
        // return
        const res = await addQuotation(payload).unwrap();
        const created = res?.data ?? res;
        const qId = created?._id;

if (qId) {
  setPrintPrompt({ open: true, quotationId: qId });
}
        toast.success("Quotation created.");
        reset(DEFAULTS);
        hydratedIdRef.current = null;

        // ✅ reset ke baad branch pe focus (customer disabled hoga until branch)
        setTimeout(() => {
          focusBranchSelect();
        }, 0);
      }
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.error ||
        err?.message ||
        "Failed to save quotation";
      toast.error(msg);
    }
  };

  const openQuotationPreview = async (id) => {
  if (!id) return;

  try {
    setQuotationPreview({ open: true, html: "", loading: true });

    const htmlContent = await downloadQuotationPdf(id).unwrap();

    setQuotationPreview({
      open: true,
      html: htmlContent,
      loading: false,
    });
  } catch (err) {
    toast.error("Failed to load quotation preview.");
    setQuotationPreview({ open: false, html: "", loading: false });
  }
};

const closeQuotationPreview = () => {
  setQuotationPreview({ open: false, html: "", loading: false });

  setTimeout(() => {
    if (mode === "create") {
      focusBranchSelect();
    }
  }, 80);
};

  return (
    <>
      {/* Quick Add Customer */}
      <QuickAddCustomerModal
        open={quickOpen}
        onClose={() => setQuickOpen(false)}
        selectedBranch={branchSel} // ✅ branch bhej diya (invoice jaisa)
        onAdded={async (created) => {
          // ✅ immediate optimistic select (same as invoice)
          const justAdded =
            (handleDropDowns({ key: "customer", customers: [created] }) ||
              [])[0] || null;
          if (justAdded) onCustomerPicked(justAdded);

          // ✅ then re-pull from server (for ensured data + branch mapping)
          lastCreatedRef.current = created;

          const searchKey =
            created?.customerId ||
            created?.contact ||
            created?.customerName ||
            "";
          setTinyArgs({
            page: 1,
            limit: 3,
            sortOrder: "desc",
            sortBy: "createdAt",
            search: String(searchKey),
            branchId: branchSel?.value || undefined, // ✅ tiny fetch bhi branch-wise
          });

          try {
            await refetchCustomers();
          } catch {}

          setQuickOpen(false);
        }}
      />

      <form onSubmit={handleSubmit(onSubmit)} 
        className="space-y-3 md:space-y-1 hide-scrollbar-inv-main"
      >
        {/* Top card */}
        <div className="bg-white shadow-sm rounded-lg px-3 py-2 border">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2">
            {topFields
              ?.filter((f) => f.visibility !== false)
              .map((item, idx) => {
                const isCustomer = item?.name === "customer";
                const isBranch = item?.name === "branch";
                const isQuotationNo = item?.name === "quotationNo";
                const cellSpan = isCustomer
                  ? "sm:col-span-2 lg:col-span-2"
                  : "";
                const hasErr = !!errors?.[item.name];

                return (
                  <div key={idx} className={`min-w-0 ${cellSpan}`}>
                    <label className="block text-xs font-semibold text-slate-600 ">
                      {item?.label}{" "}
                      {item?.mandatory && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>

                    {/* TEXT */}
                    {item?.type === "text" && (
                      <input
                        {...register(item.name, {
                          required: item?.mandatory
                            ? `${item.label} is required`
                            : false,
                        })}
                        disabled={
                          !item?.visibility ||
                          item?.disabled ||
                          item?.name === "quotationNo"
                        }
                        placeholder={
                          isQuotationNo && nextNumber && mode !== "edit"
                            ? `Next: ${nextNumber}`
                            : `Enter ${item.label}`
                        }
                        className={inputCls(hasErr)}
                      />
                    )}

                    {/* NUMBER */}
                    {item?.type === "number" && (
                      <input
                        type="number"
                        {...register(item.name, {
                          required: item?.mandatory
                            ? `${item.label} is required`
                            : false,
                        })}
                        disabled={item?.name === "quotationNo"}
                        placeholder={`Enter ${item.label}`}
                        className={inputCls(hasErr)}
                      />
                    )}

                    {/* DATE */}
                    {item?.type === "date" && (
                      <input
                        type="date"
                        max={item.name === "quotationDate" ? today : undefined}
                        min={
                          item.name === "validTill"
                            ? quotationDate || undefined
                            : undefined
                        }
                        {...register(item.name, {
                          required: item?.mandatory
                            ? `${item.label} is required`
                            : false,
                          validate: (value) => {
                            if (!value) return true;

                            if (item.name === "quotationDate") {
                              return (
                                value <= today || "Future date not allowed"
                              );
                            }

                            if (item.name === "validTill") {
                              if (quotationDate && value < quotationDate) {
                                return "Valid Till cannot be before Quotation Date";
                              }
                            }
                            return true;
                          },
                        })}
                        className={inputCls(hasErr, "py-0.5", item?.type)}
                      />
                    )}

                    {/* DROPDOWN */}
                    {item?.type === "dropdown" && (
                      <Controller
                        name={item.name}
                        control={control}
                        rules={{
                          required: item?.mandatory
                            ? `${item.label} is required`
                            : false,
                        }}
                        render={({ field: { onChange, value } }) => (
                          <div className="flex items-center gap-2">
                            <div className="w-full">
                              <Select
                                ref={
                                  isBranch
                                    ? branchSelectRef
                                    : isCustomer
                                      ? customerSelectRef
                                      : null
                                }
                                value={value}
                                options={handleDropDowns({
                                  key: item["name"],
                                  categories: categoriesFromState,
                                  products: productsFromState,
                                  designs: designFromState,
                                  purities: purityFromState,
                                  category: "",
                                  product: "",
                                  branches: branchesFromState,
                                  customers,
                                })}
                                styles={
                                  isCustomer
                                    ? withErrorBorder(
                                        customerSelectStyles,
                                        hasErr,
                                      )
                                    : withErrorBorder(baseSelectStyles, hasErr)
                                }
                                classNamePrefix="react-select"
                                menuPlacement="bottom"
                                isClearable
                                isDisabled={isCustomer && !branchSel?.value} // ✅ branch first
                                placeholder={
                                  isCustomer && !branchSel?.value
                                    ? "Select branch first"
                                    : `Select ${item.label}`
                                }
                                onChange={(opt) => {
                                  onChange(opt ?? null);
                                  if (isCustomer) onCustomerPicked(opt);
                                }}
                                onInputChange={(input, meta) => {
                                  if (!isCustomer) return;
                                  if (meta.action === "input-change")
                                    setCustomerSearch(input.trim());
                                }}
                                isLoading={isCustomer && loadingCustomers}
                                filterOption={(candidate, raw) => {
                                  if (!raw) return true;
                                  const text = (candidate?.label || "")
                                    .toString()
                                    .toLowerCase();
                                  return text.includes(raw.toLowerCase());
                                }}
                              />
                            </div>

                            {isCustomer && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!branchSel?.value) {
                                      toast.error("Select branch first.");
                                      return;
                                    }
                                    setQuickOpen(true);
                                  }}
                                  className="px-4 py-1.5 rounded-md border text-xs text-[#6b59c6] hover:bg-violet-50 focus:outline-none focus:ring-2 focus:ring-[#9481ce]"
                                  title="Quick add customer"
                                >
                                  +
                                </button>

                                <CustomerAvatarHoverCard
                                  size="md"
                                  tone="violet"
                                  customer={customer}
                                  info={{
                                    name: customer?.info?.customerName,
                                    code: customer?.info?.customerId,
                                    phone: customer?.info?.customerId,
                                    email: customer?.info?.email,
                                    address:
                                      `${customer?.permanantAddress?.addressLine1 || ""} ` +
                                      `${customer?.info?.city || ""} ` +
                                      `${customer?.info?.state || ""} ` +
                                      `${customer?.info?.country || ""}`,
                                    deliveryAddress: (() => {
                                      const def =
                                        customer?.info?.deliveryAddresses?.find(
                                          (d) => d.isDefault,
                                        ) ||
                                        customer?.info?.deliveryAddresses?.[0];
                                      if (!def) return "";
                                      return `${def.addressLine1 || ""} ${def.city || ""} ${
                                        def.state || ""
                                      } ${def.country || ""} ${def.pincode || ""}`;
                                    })(),
                                    since: customer
                                      ? formatDate(customer?.info?.createdAt)
                                      : "",
                                    fineGold: customer?.info?.fineGold,
                                    fineSilver: customer?.info?.fineSilver,
                                    walletAmount: customer?.info?.balanceAmount,
                                    outstandingAmount:
                                      customer?.info?.outstandingAmount,
                                  }}
                                />
                              </>
                            )}
                          </div>
                        )}
                      />
                    )}
                  </div>
                );
              })}
          </div>
        </div>

        {/* Item type tabs + Bulk + Summary */}
        <div className="flex items-center justify-between pb-0 mb-0">
          <InvoiceItemTypeTabs
            value={itemType}
            onChange={(opt) => setItemType(opt)}
            className="mx-0"
            handleAddRow={handleAddRow}
          />

          {itemType === "ornaments" && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className="relative  inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-white shadow-sm px-1 py-1"
              ref={summaryRef}
            >
              <input
                type="text"
                title="Rate for all"
                placeholder="Rate for all"
                value={bulkRate}
                onChange={(e) => setBulkRate(e.target.value)}
                onBlur={applyRateForAll}
                onKeyDown={(e) => e.key === "Enter" && applyRateForAll()}
                className="w-24 md:w-28 px-2 py-1 text-xs border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-violet-300"
              />

              <input
                type="text"
                title="Hallmark for all"
                placeholder="Hallmark for all"
                value={bulkHallmark}
                onChange={(e) => setBulkHallmark(e.target.value)}
                onBlur={applyHallmarkForAll}
                onKeyDown={(e) => e.key === "Enter" && applyHallmarkForAll()}
                className="w-24 md:w-32 px-2 py-1 text-xs border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-violet-300"
              />

              <button
                type="button"
                title="Overall summary"
                onClick={() => setSumOpen((s) => !s)}
                className="px-1 md:px-2 py-1.5 text-[11px] rounded-lg transition-colors text-slate-600 hover:text-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
                aria-expanded={sumOpen}
                aria-haspopup="dialog"
              >
                <FaCartShopping />
              </button>

              {sumOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-[calc(100%+10px)] right-0 min-w-[22rem] rounded-2xl border border-slate-200 bg-white shadow-2xl p-0 text-xs z-[1000]"
                  role="dialog"
                  aria-labelledby="orn-sum-title"
                >
                  <span
                    className="absolute -top-2 right-4 h-4 w-4 rotate-45 border border-slate-200 border-b-0 border-r-0 shadow-sm bg-[#9481ce]"
                    aria-hidden
                  />

                  <div className="relative px-3 py-2 rounded-t-2xl bg-[#9481ce] text-white border border-1 border-[#9481ce]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-white/20">
                          <FaCartShopping className="text-white text-[12px]" />
                        </span>
                        <span
                          id="orn-sum-title"
                          className="font-semibold text-[12.5px] tracking-wide"
                        >
                          Ornaments Summary
                        </span>
                      </div>
                      <span className="text-[10px] opacity-90">live</span>
                    </div>
                  </div>

                  <div className="p-3 text-slate-700 tabular-nums border border-1 border-[#9481ce]">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Items</span>
                        <span className="font-medium">{ornSummary.rows}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Qty</span>
                        <span className="font-medium">{ornSummary.qty}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Pcs</span>
                        <span className="font-medium">{ornSummary.pieces}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Stn Pcs</span>
                        <span className="font-medium">
                          {ornSummary.stonePcs}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Dia Pcs</span>
                        <span className="font-medium">
                          {ornSummary.diamondPcs}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Total Wt</span>
                        <span className="font-semibold">
                          {ornSummary.totalWt.toFixed(3)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Packing Wt</span>
                        <span className="font-medium">
                          {ornSummary.packingWt.toFixed(3)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Gross Wt</span>
                        <span className="font-semibold">
                          {ornSummary.grossWt.toFixed(3)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Stn Wt</span>
                        <span className="font-medium">
                          {ornSummary.stoneWt.toFixed(3)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Dia Wt (g)</span>
                        <span className="font-medium">
                          {ornSummary.diamondGram.toFixed(3)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Net Wt</span>
                        <span className="font-semibold">
                          {ornSummary.netWt.toFixed(3)}
                        </span>
                      </div>

                      <div className="col-span-2 my-1 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Stn Amt</span>
                        <span className="font-medium">
                          ₹ {ornSummary.stoneAmt.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Dia Amt</span>
                        <span className="font-medium">
                          ₹ {ornSummary.diamondAmt.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Hallmark</span>
                        <span className="font-medium">
                          ₹ {ornSummary.hallmarkAmt.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Item Amt</span>
                        <span className="font-semibold">
                          ₹ {ornSummary.itemAmt.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Tax</span>
                        <span className="font-medium">
                          ₹ {ornSummary.itemGstAmt.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">MRP</span>
                        <span className="font-medium">
                          ₹ {ornSummary.mrp.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Final Amt</span>
                        <span className="font-semibold">
                          ₹ {ornSummary.finalAmt.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
                      <span>Auto-updates with rows</span>
                      <span className="px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-700">
                        preview
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}
        </div>

        {/* Items area */}
        <div className="bg-white shadow-sm rounded-xl p-0 mb-2 z-10">
          {itemType === "ornaments" && (
            <div
              ref={ornamentsListRef}
              className={`relative ${CARD_HEIGHT_CLASS} overflow-y-auto scroll-smooth snap-y snap-mandatory hide-scrollbar-inv-items`}
            >
              {ornamentFields?.map((field, idx) => (
                <InvoiceItemForm
                  key={field.id}
                  control={control}
                  register={register}
                  unregister={unregister}
                  watch={watch}
                  setValue={setValue}
                  getValues={getValues}
                  errors={errors}
                  rowindex={idx}
                  fieldId={field.id}
                  mode={mode}
                  showDelete={mode !== "edit" && allowMultipleRows}
                  onDelete={
                    mode !== "edit" && allowMultipleRows
                      ? () => {
                          const targetIdx = Math.max(0, idx - 1);
                          const rowPath = `stockEntries.${idx}`;
                          try {
                            unregister(rowPath);
                          } catch {}
                          clearErrors(rowPath);
                          ornamentRemove(idx);
                          requestAnimationFrame(() =>
                            setTimeout(
                              () => focusRowAt(ornamentsListRef, targetIdx),
                              0,
                            ),
                          );
                        }
                      : undefined
                  }
                  cardHeightClass={CARD_HEIGHT_CLASS}
                  registerFirstField={registerFirstField}
                  selectedBranch={branchSel}
                  manualAdjustmentMode={manualAdjustmentMode}
                />
              ))}

              {ornamentFields.length === 0 && (
                <motion.div
                  tabIndex={-1}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0 }}
                  className="m-2 p-4 border border-gray-200 hover:border-gray-300 shadow-lg rounded-lg bg-white text-sm text-gray-600"
                >
                  No rows. Click “Add Row”.
                </motion.div>
              )}
            </div>
          )}

          {itemType === "loose" && (
            <div
              ref={looseStoneListRef}
              className={`relative ${CARD_HEIGHT_CLASS} overflow-y-auto scroll-smooth snap-y snap-mandatory`}
            >
              {looseFields.map((field, idx) => (
                <LooseStoneItemForm
                  key={field.id}
                  control={control}
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  getValues={getValues}
                  errors={errors}
                  rowindex={idx}
                  fieldId={field.id}
                  showDelete={mode !== "edit" && allowMultipleRows}
                  onDelete={
                    mode !== "edit" && allowMultipleRows
                      ? () => {
                          const targetIdx = Math.max(0, idx - 1);
                          const rowPath = `looseStones.${idx}`;
                          try {
                            unregister(rowPath);
                          } catch {}
                          clearErrors(rowPath);
                          looseRemove(idx);
                          requestAnimationFrame(() =>
                            setTimeout(
                              () => focusRowAt(looseStoneListRef, targetIdx),
                              0,
                            ),
                          );
                        }
                      : undefined
                  }
                  cardHeightClass={CARD_HEIGHT_CLASS}
                  registerFirstField={registerFirstField}
                  selectedBranch={branchSel}
                />
              ))}

              {looseFields.length === 0 && (
                <motion.div
                  tabIndex={-1}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0 }}
                  className="m-2 p-4 border border-gray-200 hover:border-gray-300 shadow-lg rounded-lg bg-white text-sm text-gray-600"
                >
                  No rows. Click “Add Row”.
                </motion.div>
              )}
            </div>
          )}

          {itemType === "looseDiamond" && (
            <div
              ref={looseDiamondListRef}
              className={`relative ${CARD_HEIGHT_CLASS} overflow-y-auto scroll-smooth snap-y snap-mandatory`}
            >
              {looseDiaFields.map((field, idx) => (
                <LooseDiamondItemForm
                  key={field.id}
                  control={control}
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  getValues={getValues}
                  errors={errors}
                  rowindex={idx}
                  fieldId={field.id}
                  showDelete={mode !== "edit" && allowMultipleRows}
                  onDelete={
                    mode !== "edit" && allowMultipleRows
                      ? () => {
                          const targetIdx = Math.max(0, idx - 1);
                          const rowPath = `looseDiamonds.${idx}`;
                          try {
                            unregister(rowPath);
                          } catch {}
                          clearErrors(rowPath);
                          looseDiaRemove(idx);
                          requestAnimationFrame(() =>
                            setTimeout(
                              () => focusRowAt(looseDiamondListRef, targetIdx),
                              0,
                            ),
                          );
                        }
                      : undefined
                  }
                  cardHeightClass={CARD_HEIGHT_CLASS}
                  registerFirstField={registerFirstField}
                  selectedBranch={branchSel}
                />
              ))}

              {looseDiaFields.length === 0 && (
                <motion.div
                  tabIndex={-1}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0 }}
                  className="m-2 p-4 border border-gray-200 hover:border-gray-300 shadow-lg rounded-lg bg-white text-sm text-gray-600"
                >
                  No rows. Click “Add Row”.
                </motion.div>
              )}
            </div>
          )}
        </div>

        {/* Summary footer */}
        <div className="rounded-lg">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* LEFT */}
            <div className="space-y-2">
              <div className="border rounded-lg px-3 py-2 bg-white text-xs leading-relaxed">
                <div className="mb-1">
                  <span className="text-slate-700 font-semibold">
                    Quotation Notes / Purpose
                  </span>
                </div>
                <p className="text-slate-500 text-[11px]">
                  Quotation is for discussion / approval only. Rates are based
                  on current bullion &amp; GST and may change without notice.
                </p>
                <ul className="mt-1 list-disc list-inside space-y-0.5 text-[11px] text-slate-500">
                  <li>
                    Final invoice amount will be recalculated on the date of
                    confirmation / billing based on the live rate and agreed
                    making charges.
                  </li>
                  <li>
                    Stones / diamonds, hallmark, courier and other charges shown
                    here are indicative &amp; subject to final verification at
                    the time of sale.
                  </li>
                  <li>
                    This document is not a tax invoice and does not create any
                    legal obligation until converted to a confirmed order /
                    invoice.
                  </li>
                </ul>
              </div>
            </div>

            {/* RIGHT */}
            <div className="border rounded-lg px-2 py-2 bg-white">
<div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 sm:gap-x-8 gap-y-2 text-xs">               
<div className="flex items-start sm:items-center justify-between gap-2">                  <span className="text-slate-500 font-bold">
                    TAXABLE AMOUNT (pre-tax)
                  </span>
                  <span className="font-medium text-blue-600">
                    ₹ {taxableAmount.toFixed(2)}
                  </span>
                </div>

<div className="flex items-start sm:items-center justify-between gap-2">                  <span className="text-slate-500 font-bold">
                    MRP ITEMS (incl. tax)
                  </span>
                  <span className="font-medium text-blue-600">
                    ₹{" "}
                    {(
                      agg.ornMrpFinal +
                      agg.stoneMrpFinal +
                      agg.diaMrpFinal
                    ).toFixed(2)}
                  </span>
                </div>

<div className="flex items-start sm:items-center justify-between gap-2">                  <span className="text-slate-500 font-bold">DISCOUNT</span>
                  <input
                    type="text"
                    {...register("totalDiscount")}
                    className="w-32 px-2 py-0 my-0 border rounded-md text-right font-medium"
                    placeholder="0.00"
                  />
                </div>

<div className="flex items-start sm:items-center justify-between gap-2">                  <span className="text-slate-500 font-bold">
                    SALE (pre-tax)
                  </span>
                  <span className="font-medium text-blue-600">
                    ₹{" "}
                    {(
                        salePreTax
                      ).toFixed(2)}
                  </span>
                </div>

<div className="flex items-start sm:items-center justify-between gap-2">                  <span className="text-slate-500 font-bold">
                    GST{" "}
                    <span className="text-[8px]">
                      ({" " + ORNAMENT_GST + "% "})
                      {/* / Stones{" "} */}
                      {/* {gstLooseWeightedRate.toFixed(2)}% / Diamond{" "}
                      {gstDiaWeightedRate.toFixed(2)}%) */}
                    </span>
                  </span>
                  <span className="font-medium text-blue-600">
                    ₹ {(gstOrn + gstLoose + gstDia).toFixed(2)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-slate-500 font-bold">COURIER</span>
                  <input
                    type="text"
                    {...register("courierCharge")}
                    className="w-32 px-2 py-0 my-0 border rounded-md text-right font-medium"
                    placeholder="0.00"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-slate-500 font-bold">TOTAL AMOUNT</span>
                  <span className="font-semibold text-blue-600">
                    ₹ {computedGrandTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="space-y-2 bg-white rounded-xl p-2 mt-1 border">
<div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap gap-2 lg:justify-end items-stretch lg:items-center mt-1">
                {mode !== "edit" && allowMultipleRows && (
                <motion.button
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98, y: 0 }}
                  type="button"
                  onClick={handleAddRow}
className="group relative w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl sm:rounded-full border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] font-medium text-sky-700 shadow-sm hover:bg-sky-100 hover:border-sky-300 hover:shadow-md transition-all whitespace-nowrap"                >
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-sky-500 text-white">
                    <FaPlus className="text-[10px]" />
                  </span>
                  <span className="tracking-wide">Add Row</span>

<span className="ml-1 h-4 w-px bg-sky-200 group-hover:bg-sky-300 hidden md:block" />
                 <span className="hidden md:inline-flex items-center rounded-full bg-white/80 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide text-sky-700">
  Ctrl + +
</span>
                </motion.button>
              )}

              <motion.button
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98, y: 0 }}
                type="submit"
className="group relative w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl sm:rounded-full bg-emerald-500 px-3 py-2 text-[11px] font-semibold text-white shadow-sm hover:bg-emerald-600 hover:shadow-md transition-all disabled:opacity-70 disabled:cursor-not-allowed whitespace-nowrap"                disabled={addState.isLoading || updateState.isLoading}
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                  <FaCheck className="text-[10px]" />
                </span>
                <span className="tracking-wide">
                  {mode === "edit" ? "Update" : "Save"}
                </span>

<span className="ml-1 h-4 w-px bg-emerald-300/80 group-hover:bg-white/60 hidden md:block" />
               <span className="hidden md:inline-flex items-center rounded-full bg-emerald-400/90 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide">
  Ctrl + S
</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98, y: 0 }}
                type="button"
                onClick={() =>
                  reset(
                    mode === "edit" && existingData ? existingData : DEFAULTS,
                  )
                }
className="group relative w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl sm:rounded-full border border-slate-300 bg-slate-50 px-3 py-2 text-[11px] font-semibold text-slate-700 shadow-sm hover:bg-slate-100 hover:border-slate-400 hover:shadow-md transition-all whitespace-nowrap"              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-700 text-white">
                  <FaUndo className="text-[10px]" />
                </span>
                <span className="tracking-wide">Reset</span>

<span className="ml-1 h-4 w-px bg-slate-200 group-hover:bg-slate-300 hidden md:block" />
                <span className="hidden md:inline-flex items-center rounded-full bg-white/80 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide text-slate-700">
  Ctrl + R
</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98, y: 0 }}
                type="button"
                onClick={() => navigate("/quotations/quotation-list")}
className="group relative w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl sm:rounded-full bg-indigo-500 px-3 py-2 text-[11px] font-semibold text-white shadow-sm hover:bg-indigo-600 hover:shadow-md transition-all whitespace-nowrap"              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                  <FaList className="text-[10px]" />
                </span>
                <span className="tracking-wide">Quotation List</span>

<span className="ml-1 h-4 w-px bg-indigo-300/80 group-hover:bg-white/60 hidden md:block" />
                <span className="hidden md:inline-flex items-center rounded-full bg-indigo-400/90 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide">
  Ctrl + ⌫
</span>
              </motion.button>
            </div>
          </div>
        </div>
      </form>
      <ConfirmDialog
  open={printPrompt.open}
  onCancel={() => {
    setPrintPrompt({ open: false, quotationId: null });

    setTimeout(() => {
      if (mode === "create") {
        focusBranchSelect();
      }
    }, 0);
  }}
  onConfirm={() => {
    const id = printPrompt.quotationId;
    setPrintPrompt({ open: false, quotationId: null });

    if (id) {
      openQuotationPreview(id);
    }
  }}
  title="Quotation saved successfully"
  message="Would you like to open the quotation preview for printing?"
  confirmText="Yes, open & print"
  cancelText="No"
  loading={false}
  destructive={false}
/>

<InvoicePrintPreviewModal
  open={quotationPreview.open}
  html={quotationPreview.html}
  loading={quotationPreview.loading}
  onClose={closeQuotationPreview}
/>
    </>
  );
};

export default React.memo(QuotationForm);
